import React, { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';

import { category, categoryVariables } from 'src/queries/__generated__/category';
import { useDeps } from 'src/hooks/useDeps';
import { Breadcrumb, cleanBreadcrumbs } from 'src/components/Breadcrumbs/breadcrumb.utils';

import categoryQuery from '../../queries/category.graphql';

interface CategoryQueryContextType {
    name: string;
    description: string | null;
    meta_description: string | null;
    meta_title: string | null;
    category_id: number;
    title: string;
    urlKey: string;
    loading: boolean;
    error: ApolloError | undefined;
    breadcrumbs: Breadcrumb[];
    blogIds?: string[];
}

const initialData = {
    name: '',
    description: '',
    meta_description: '',
    meta_title: '',
    category_id: -1,
    title: '',
    urlKey: '',
    loading: false,
    error: undefined,
    breadcrumbs: [],
    blogIds: [],
};

export const CategoryQueryContext = React.createContext<CategoryQueryContextType>(initialData);

export const CategoryQueryProvider: React.FC<{ id: number }> = React.memo((props) => {
    const { loading, error, data } = useQuery<category, categoryVariables>(categoryQuery, {
        variables: {
            id: props.id,
        },
        ssr: useDeps().env.SSR_GQL,
    });

    const api: CategoryQueryContextType = useMemo(() => {
        if (!data || !data.category) {
            return {
                ...initialData,
                blog_ids: [],
                error,
                loading,
            };
        }

        const {
            blog_ids: raw_blog_ids,
            breadcrumbs,
            description,
            meta_description,
            meta_title,
            name: category_name,
            url_path,
        } = data.category;

        // Clean up the blogIds from the dataset
        // Start with undefined
        let blogIds: string[] | undefined = undefined;

        // If the var isn't null, filter out any possible null values, whilst ensuring it with a custom type guard
        if (raw_blog_ids !== null) {
            blogIds = raw_blog_ids?.filter((item): item is string => item !== null);
        }

        return {
            blogIds,
            breadcrumbs: cleanBreadcrumbs(breadcrumbs),
            category_id: props.id,
            description,
            error,
            loading,
            meta_description,
            meta_title,
            name: category_name || 'unknown',
            title: meta_title || category_name || 'unknown',
            urlKey: url_path || 'unknown',
        };
    }, [data, error, loading, props.id]);

    return <CategoryQueryContext.Provider value={api}>{props.children}</CategoryQueryContext.Provider>;
});
